import dayjs from "dayjs"
import { FC, useEffect, useMemo, useState } from "react"
import {
  FlyoutProps,
  VictoryAxis,
  VictoryChart,
  VictoryContainer,
  VictoryLine,
  VictoryScatter,
  VictoryTooltip,
  VictoryLabel,
} from "victory"
import {
  GetQuestionnaireScoringBoundsDocument,
  GetQuestionnaireScoringBoundsQuery,
  GetQuestionnaireScoringBoundsQueryVariables,
} from "../../generated/graphql"
import { useGraphQLClient } from "../../hooks/useGraphQLClient"
import { colors } from "../../utils/theme"
import { ChartDataPoints, ChartLines } from "../../utils/types"
import { formatDisplayScore } from "../../utils/formatters"

const ChartTooltip: FC<
  {
    datum?: ChartDataPoints
    otherData?: ChartDataPoints[][][]
    otherDataRange?: number
    scoreType: string
  } & FlyoutProps
> = ({ x, y, datum, scoreType }) => {
  if (!datum || !x || !y) {
    return null
  }

  const score =
    datum.y != null ? formatDisplayScore(datum.y, scoreType) : datum?.y
  const estimatedRequiredWidth = ("" + datum?.other).length * 8
  const width = estimatedRequiredWidth > 144 ? estimatedRequiredWidth + 12 : 156

  return (
    <g>
      <rect
        x={x}
        y={(y ?? 0) + 20}
        height={86}
        width={width}
        rx={4}
        ry={4}
        fill={colors.neutral.space}
      />
      <text
        x={(x ?? 0) + 12}
        y={(y ?? 0) + 46}
        strokeWidth={0}
        fill={colors.neutral.white}
        fontFamily="Lato"
        fontSize={14}
      >
        {scoreType} Score: {score}
      </text>
      <text
        x={(x ?? 0) + 12}
        y={(y ?? 0) + 68}
        strokeWidth={0}
        fill={colors.neutral.white}
        fontFamily="Lato"
        fontWeight={400}
        fontSize={14}
      >
        {datum?.other}
      </text>
      <text
        x={(x ?? 0) + 12}
        y={(y ?? 0) + 88}
        strokeWidth={0}
        fill={colors.neutral.white}
        fontStyle="italic"
        fontFamily="Lato"
        fontWeight={400}
        fontSize={14}
      >
        {datum?.date}
      </text>
    </g>
  )
}

const QuestionnaireChart: FC<{
  id: number
  data: ChartDataPoints[]
  scoreType: string
}> = ({ id, data, scoreType }) => {
  const graphQLClient = useGraphQLClient()

  const [bounds, setBounds] = useState<
    GetQuestionnaireScoringBoundsQuery | undefined
  >(undefined)

  useEffect(() => {
    if (id) {
      const fetchScoringBounds = async () => {
        try {
          const resp = await graphQLClient.request<
            GetQuestionnaireScoringBoundsQuery,
            GetQuestionnaireScoringBoundsQueryVariables
          >(GetQuestionnaireScoringBoundsDocument, {
            questionnaireId: id,
          })
          setBounds(resp)
        } catch (error) {
          return
        }
      }
      fetchScoringBounds()
    }
  }, [graphQLClient, id])

  // Derives a stable y-axis domain and evenly spaced tick marks from scoring bounds.
  const yAxisConfig = useMemo(() => {
    if (bounds === undefined || bounds.questionnaire_scoring.length === 0) {
      return {
        min: undefined,
        max: undefined,
        ticks: [] as ChartLines,
      }
    }

    // Grab global min / max from all scoring bands
    const min = Math.min(
      ...bounds.questionnaire_scoring.map((bound) => bound.lower_bound)
    )
    const max = Math.max(
      ...bounds.questionnaire_scoring.map((bound) => bound.upper_bound)
    )

    if (min === max) {
      return {
        min,
        max,
        ticks: [min],
      }
    }

    // Create 5 evenly spaced ticks between the min and max scoring values
    const tickCount = 5
    const step = (max - min) / (tickCount - 1)
    const ticks: ChartLines = Array.from({ length: tickCount }, (_, index) =>
      Number((min + step * index).toFixed(2))
    )

    ticks[0] = min
    ticks[ticks.length - 1] = max

    return {
      min,
      max,
      ticks,
    }
  }, [bounds])

  return (
    <VictoryChart
      height={285}
      width={385}
      domainPadding={{ x: [24, 24] }}
      domain={
        yAxisConfig.min !== undefined && yAxisConfig.max !== undefined
          ? { y: [yAxisConfig.min, yAxisConfig.max] }
          : undefined
      }
      padding={{ top: 16, bottom: 100, left: 63, right: 8 }} // left padding accounts for the hundreds space in y-axis labels
      containerComponent={<VictoryContainer style={{ height: "auto" }} />}
    >
      <VictoryAxis
        orientation="top"
        tickComponent={<></>}
        tickLabelComponent={<></>}
        style={{
          axis: { stroke: colors.neutral.clay },
        }}
      />
      <VictoryAxis
        orientation="right"
        dependentAxis={true}
        tickComponent={<></>}
        tickLabelComponent={<></>}
        style={{
          axis: { stroke: colors.neutral.clay },
        }}
      />
      <VictoryAxis
        orientation="bottom"
        tickFormat={(tick: string | number | Date) =>
          dayjs(tick).format("MMM D")
        }
        tickLabelComponent={
          <VictoryLabel angle={-75} textAnchor="end" verticalAnchor="middle" />
        }
        style={{
          tickLabels: {
            fontFamily: "Lato",
            color: colors.neutral.asphalt,
            fontSize: 12,
            fontWeight: 400,
            padding: 10,
          },
          axis: { stroke: colors.neutral.clay },
          ticks: { stroke: "transparent" },
        }}
      />
      <VictoryAxis
        orientation="left"
        dependentAxis={true}
        label={scoreType ? `${scoreType} Score` : undefined}
        axisLabelComponent={<VictoryLabel angle={-90} />}
        tickValues={yAxisConfig.ticks}
        tickFormat={(tick: string | number) => {
          if (typeof tick !== "number") {
            return tick
          }

          return Number.isInteger(tick) ? tick : tick.toFixed(2)
        }}
        style={{
          tickLabels: {
            fontFamily: "Lato",
            color: colors.neutral.asphalt,
            fontSize: 12,
            fontWeight: 400,
          },
          axisLabel: {
            fontFamily: "Lato",
            color: colors.neutral.asphalt,
            fontSize: 12,
            fontWeight: 700,
            padding: 48,
          },
          grid: { stroke: "transparent" },
          axis: { stroke: colors.neutral.clay },
        }}
      />

      <VictoryLine
        data={data.map(({ x, y }) => ({ x, y }))}
        style={{
          data: {
            stroke: colors.neutral.cement,
            strokeWidth: 2,
          },
        }}
      />

      <VictoryScatter
        data={data}
        symbol="circle"
        size={6}
        style={{
          data: {
            strokeWidth: 2,
            fill: ({ datum }) => datum.fill,
            stroke: ({ datum }) => datum.stroke,
          },
        }}
        labelComponent={
          <VictoryTooltip
            flyoutComponent={<ChartTooltip scoreType={scoreType} />}
          />
        }
      />
    </VictoryChart>
  )
}

export default QuestionnaireChart
